import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaPaperPlane,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import "../../Component/Home/theme.css"; // Import the CSS file

// Franklin, NJ timezone (EST/EDT)
const FRANKLIN_TIMEZONE = 'America/New_York';

// Get current date in Franklin, NJ
// ✅ Safe, timezone-correct, date-only
const getFranklinDate = () => {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: FRANKLIN_TIMEZONE
  });
};




// Format date to YYYY-MM-DD for input field (using Franklin date)

// Parse date string to Franklin Date object
const parseFranklinDate = (dateString) => {
  if (!dateString) return null;
  return new Date(`${dateString}T00:00:00`);
};


// Get current date & time in Franklin, NJ
const getFranklinDateTime = () => {
  return new Date().toLocaleString('en-US', {
    timeZone: FRANKLIN_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Get tomorrow's date in Franklin


// Time slots available (Tuesday-Friday 10-5, Saturday 10-2)
const timeSlots = [
  '10:00 AM to 12:00 PM',
  '12:00 PM to 02:00 PM',
  '02:00 PM to 04:00 PM'
];

// Saturday-only time slots
const saturdayTimeSlots = [
  '10:00 AM to 12:00 PM',
  '12:00 PM to 02:00 PM'
];

const ContactForm = ({ onSuccess, className = '' }) => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeField, setActiveField] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  
  // Form data structure
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: 'USA',
      zipCode: ''
    },
    subject: 'Kitchen Cabinet Inquiry',
    message: '',
    sendCopy: true
  });

  // Get today's date in Franklin for display
  const todayFranklin = getFranklinDate();
  const formattedToday = new Date(
  `${todayFranklin}T00:00:00`
).toLocaleDateString('en-US', {
  timeZone: FRANKLIN_TIMEZONE,
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});

  // Function to get minimum date (tomorrow in Franklin)
  const getMinDate = () => {
  const today = parseFranklinDate(getFranklinDate());
  today.setDate(today.getDate() + 1);
  return today.toISOString().split('T')[0];
};

  // Function to get maximum date (60 days from now in Franklin)
  const getMaxDate = () => {
  const future = parseFranklinDate(getFranklinDate());
  future.setDate(future.getDate() + 60);
  return future.toISOString().split('T')[0];
};

  // Function to check if a date is Monday or Sunday (in Franklin)
 const isClosedDay = (dateString) => {
  const franklinDate = parseFranklinDate(dateString);
  const day = franklinDate.getDay();
  return day === 0 || day === 1;
};

  // Function to check if date is Saturday (in Franklin)
  const isSaturday = (dateString) => {
  const franklinDate = parseFranklinDate(dateString);
  return franklinDate.getDay() === 6;
};


  // Function to check if a date is in the past (including today) in Franklin
  const isPastDate = (dateString) => {
  const today = parseFranklinDate(getFranklinDate());
  const checkDate = parseFranklinDate(dateString);

  return checkDate < today;
};


  // Function to get day name from date (in Franklin)
  const getDayName = (dateString) => {
  const franklinDate = parseFranklinDate(dateString);
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  return days[franklinDate.getDay()];
};

  // Get time slots based on selected date
  const getAvailableTimeSlots = () => {
    if (!formData.preferredDate) return timeSlots;
    if (isSaturday(formData.preferredDate)) {
      return saturdayTimeSlots;
    }
    return timeSlots;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    // Email is optional but must be valid if provided
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    // Phone validation - strict requirement
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]{10,20}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Enter a valid phone number (10-20 digits, international format accepted)';
    } else {
      // Extract only digits for length check
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        errors.phone = 'Phone number must have at least 10 digits';
      } else if (digitsOnly.length > 20) {
        errors.phone = 'Phone number cannot exceed 20 digits';
      }
    }

    // Check if preferred date is Monday or Sunday
    if (formData.preferredDate && isClosedDay(formData.preferredDate)) {
      const dayName = getDayName(formData.preferredDate);
      errors.preferredDate = `We are closed on ${dayName}s. Please select another date.`;
    }

    // Check if time is selected but no date
    if (formData.preferredTime && !formData.preferredDate) {
      errors.preferredDate = 'Please select a date first';
    }

    if (formData.message && formData.message.length < 5) {
      errors.message = 'Message must be at least 5 characters';
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    
    // Check if selected date is in the past (including today) in Franklin
   const today = parseFranklinDate(getFranklinDate());
const selectedFranklin = parseFranklinDate(selectedDate);

if (selectedFranklin < today) {
  toast.error('Please select a future date (tomorrow or later)');
  return;
}
    // Check if selected date is Monday or Sunday
    if (isClosedDay(selectedDate)) {
      const dayName = getDayName(selectedDate);
      toast.error(`We are closed on ${dayName}s. Please select another date.`);
      setFormData(prev => ({
        ...prev,
        preferredDate: '',
        preferredTime: '' // Clear time if closed day is selected
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      preferredDate: selectedDate,
      preferredTime: '' // Reset time when date changes
    }));
    setSelectedDate(selectedDate);
  };

  const handleTimeChange = (e) => {
    const selectedTime = e.target.value;
    setFormData(prev => ({
      ...prev,
      preferredTime: selectedTime
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setSubmitError(null);
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach(error => {
        if (error) toast.error(error);
      });
      return;
    }

    setIsSubmitting(true);

    // Debug log
    console.log('Submitting form data:', formData);

    // Prepare data for backend - keep phone format as entered
    const submissionData = {
      ...formData,
      phone: formData.phone.trim(), // Keep original formatting
      preferredDate: formData.preferredDate || null,
      preferredTime: formData.preferredTime || '',
      timestamp: new Date().toISOString()
    };

    try {
      console.log('Calling API with:', submissionData);
      
      const response = await apiService.submitContact(submissionData);
      
      console.log('API Response:', response);
      
      if (response.success) {
        toast.success('Message sent successfully! We will contact you within 24 hours.');
        
        // Prepare submission data BEFORE resetting form
        const submissionDataForThankYou = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          subject: formData.subject,
          message: formData.message,
          address: formData.address,
          ...(response.data && { ...response.data }),
          submittedAt: getFranklinDateTime()
        };
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          preferredDate: '',
          preferredTime: '',
          address: {
            street: '',
            city: '',
            state: '',
            country: 'USA',
            zipCode: ''
          },
          subject: 'Kitchen Cabinet Inquiry',
          message: '',
          sendCopy: true
        });

        setSelectedDate('');

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(submissionDataForThankYou);
        } else {
          // Default behavior - navigate to thank you page
          setTimeout(() => {
            navigate('/thank-you', { 
              state: { 
                submissionData: submissionDataForThankYou
              } 
            });
          }, 1500);
        }

      } else {
        const errorMessage = response.message || 'Failed to send message. Please try again.';
        setSubmitError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.message || 'Network error. Please check your connection and try again.';
      setSubmitError(errorMessage);
      
      // Provide more specific error messages
      if (error.response) {
        toast.error(`Server error: ${error.response.status}`);
      } else if (error.request) {
        toast.error('No response from server. Please try again later.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Available time slots
  const availableTimeSlots = getAvailableTimeSlots();

  return (
    <div className={className} ref={formRef}>
      {/* Today's Date Display (Franklin, NJ) */}
      

      {/* Error Display */}
      {submitError && (
        <div className="mb-4 p-4 rounded-xl max-w-md mx-auto" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
          <p className="inter-secondary font-medium" style={{ color: "rgb(239, 68, 68)" }}>Error: {submitError}</p>
          <p className="inter-secondary text-sm mt-1" style={{ color: "rgb(239, 68, 68)" }}>Please try again or call us directly at (862) 450-6069</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-semibold mb-2 inter-secondary" style={{ color: "var(--color-primary-500)" }}>
              Full Name *
            </label>
            <div className="relative">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setActiveField('name')}
                onBlur={() => setActiveField('')}
                required
                className="w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
                style={{
                  borderColor: activeField === 'name' ? "var(--color-primary-500)" : "var(--border-medium)",
                  color: "var(--color-black)",
                }}
                placeholder="John Smith"
              />
            </div>
          </div>

          {/* Email Field - Optional */}
          <div>
            <label className="block text-sm font-semibold mb-2 inter-secondary" style={{ color: "var(--color-primary-500)" }}>
              Email Address (Optional)
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setActiveField('email')}
                onBlur={() => setActiveField('')}
                className="w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
                style={{
                  borderColor: activeField === 'email' ? "var(--color-primary-500)" : "var(--border-medium)",
                  color: "var(--color-black)",
                }}
                placeholder="john@example.com"
              />
            </div>
          </div>
        </div>

        {/* Phone Field - Required with validation */}
        <div>
          <label className="block text-sm font-semibold mb-2 inter-secondary" style={{ color: "var(--color-primary-500)" }}>
            Phone Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onFocus={() => setActiveField('phone')}
              onBlur={() => setActiveField('')}
              required
              className="w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: activeField === 'phone' ? "var(--color-primary-500)" : "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="+1 (862) 450-6069 or 8624506069"
            />
            <p className="inter-secondary text-xs mt-2" style={{ color: "var(--color-primary-500)" }}>
              Required. Enter phone number in any format (10-20 digits). International numbers accepted.
            </p>
          </div>
        </div>

        {/* Preferred Date and Time Section */}
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-primary-100)" }}>
              <span className="text-lg sm:text-xl" style={{ color: "var(--color-primary-500)" }}>📅</span>
            </div>
            <h3 className="inter-secondary text-base sm:text-lg font-semibold" style={{ color: "var(--color-primary-500)" }}>
              Preferred Consultation Date & Time (Optional)
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="relative">
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleDateChange}
                min={getMinDate()}
                max={getMaxDate()}
                className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
                style={{
                  borderColor: "var(--border-medium)",
                  color: "var(--color-black)",
                }}
              />
              {formData.preferredDate && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {isClosedDay(formData.preferredDate) ? (
                    <FaTimesCircle className="text-red-500" title="Closed on this day" />
                  ) : (
                    <FaCheckCircle className="text-green-500" title="Available" />
                  )}
                </div>
              )}
            </div>

            <div>
              <select
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleTimeChange}
                className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
                style={{
                  borderColor: "var(--border-medium)",
                  color: "var(--color-black)",
                }}
                disabled={!formData.preferredDate || isClosedDay(formData.preferredDate)}
              >
                <option value="">Select time</option>
                {availableTimeSlots.map((slot, index) => (
                  <option key={index} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Status Message */}
          {formData.preferredDate && (
            <div className={`p-3 rounded-lg text-sm ${
              isClosedDay(formData.preferredDate) 
                ? 'bg-red-50 text-red-700' 
                : 'bg-green-50 text-green-700'
            }`}>
              {isClosedDay(formData.preferredDate) ? (
                <div className="flex items-center gap-2">
                  <FaTimesCircle />
                  <span>
                    We are closed on {getDayName(formData.preferredDate)}s. 
                    Please select a date Tuesday through Saturday.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <FaCheckCircle />
                  <span>
                    {getDayName(formData.preferredDate)} is available for consultation.
                    {isSaturday(formData.preferredDate) 
                      ? ' (Saturday hours: 10:00 AM - 2:00 PM)' 
                      : ' (Tuesday-Friday hours: 10:00 AM - 5:00 PM)'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Address Section */}
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--color-primary-100)" }}>
              <span className="text-lg sm:text-xl" style={{ color: "var(--color-primary-500)" }}>🏠</span>
            </div>
            <h3 className="inter-secondary text-base sm:text-lg font-semibold" style={{ color: "var(--color-primary-500)" }}>
              Project Address (Optional)
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <input
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="Street address"
            />
            <input
              type="text"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="City"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <input
              type="text"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="State (e.g., NJ)"
            />
            <input
              type="text"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="ZIP Code"
            />
            <input
              type="text"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all text-sm sm:text-base inter-secondary"
              style={{
                borderColor: "var(--border-medium)",
                color: "var(--color-black)",
              }}
              placeholder="Country"
            />
          </div>
        </div>

        {/* Subject Field (hidden but included in form) */}
        <input type="hidden" name="subject" value={formData.subject} />

        {/* Message Field */}
        <div>
          <label className="block text-sm font-semibold mb-2 inter-secondary" style={{ color: "var(--color-primary-500)" }}>
            Project Details 
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setActiveField('message')}
            onBlur={() => setActiveField('')}
            rows="4"
            className="w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 rounded-xl outline-none transition-all resize-none text-sm sm:text-base inter-secondary"
            style={{
              borderColor: activeField === 'message' ? "var(--color-primary-500)" : "var(--border-medium)",
              color: "var(--color-black)",
            }}
            placeholder="Tell us about your project vision, timeline, and specific requirements..."
            maxLength="500"
          />
          <p className="inter-secondary text-xs mt-2" style={{ color: "var(--color-primary-500)" }}>
            {formData.message.length}/500 characters
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inter-secondary w-full font-semibold py-3 sm:py-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none text-sm sm:text-base"
          style={{
            background: "var(--gradient-primary)",
            color: "var(--color-white)",
          }}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2 sm:mr-3"></div>
              Sending Inquiry...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <FaPaperPlane className="mr-2 sm:mr-3" />
              Send Project Inquiry
            </div>
          )}
        </button>

        {/* Debug Info (remove in production) */}
        {process.env.NODE_ENV === 'development' && submitError && (
          <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: "var(--color-gray-100)" }}>
            <p className="inter-secondary text-xs" style={{ color: "var(--color-gray-600)" }}>Debug: {submitError}</p>
          </div>
        )}
      </form>

      {/* Add CSS for date input styling */}
      <style jsx>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          background: transparent;
          bottom: 0;
          color: transparent;
          cursor: pointer;
          height: auto;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: auto;
        }
        
        input[type="date"]::-webkit-datetime-edit-text,
        input[type="date"]::-webkit-datetime-edit-month-field,
        input[type="date"]::-webkit-datetime-edit-day-field,
        input[type="date"]::-webkit-datetime-edit-year-field {
          color: var(--color-black);
        }
        
        input[type="date"]:invalid::-webkit-datetime-edit {
          color: #999;
        }
      `}</style>
    </div>
  );
};

export default ContactForm;